import { useMemo, useState } from "react";
import {
    ArrowLeft,
    ChevronRight,
    KeyRound,
    Package,
    Pencil,
    ShieldX,
    Trash2,
} from "lucide-react";
import { Link, Navigate, useNavigate, useParams } from "@tanstack/react-router";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCurrentUser } from "~/lib/auth";
import { type LicenseRecord, useLicenses } from "~/lib/licenses";
import {
    type ProductStatus,
    useCapabilities,
    useDeleteProduct,
    useProduct,
    useSetProductCapabilities,
} from "~/lib/products";

import { EditProductDialog } from "~/components/EditProductDialog";
import { LicenseDetailDialog } from "~/components/LicenseDetailDialog";
import { TableSkeleton } from "~/components/TableSkeleton";

const STATUS_PILL: Record<ProductStatus, string> = {
    active: "status-pill--live",
    deprecated: "status-pill--paused",
    retired: "status-pill--failed",
};

const LICENSE_STATUS_PILL: Record<LicenseRecord["status"], string> = {
    active: "status-pill--live",
    suspended: "status-pill--paused",
    expired: "status-pill--queued",
    revoked: "status-pill--failed",
};

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

export function ProductDetailPage(): React.ReactElement {
    const me = useCurrentUser();
    const isAdmin = me.data?.roles.includes("admin");
    const { slug } = useParams({ from: "/app/products/$slug" });
    const productQ = useProduct(slug);
    const capsQ = useCapabilities();
    const licensesQ = useLicenses();
    const setCaps = useSetProductCapabilities();
    const deleteProduct = useDeleteProduct();
    const navigate = useNavigate();

    const [editOpen, setEditOpen] = useState(false);
    const [activeLicense, setActiveLicense] = useState<LicenseRecord | null>(null);

    const productLicenses = useMemo(
        () => licensesQ.data?.filter((l) => l.productSlug === slug) ?? [],
        [licensesQ.data, slug],
    );

    if (me.data && !isAdmin) {
        return <Navigate to="/" />;
    }

    if (productQ.isLoading) {
        return (
            <div className="stack-lg">
                <header className="page-header">
                    <div className="page-header__top">
                        <div className="page-header__heading">
                            <span className="page-header__eyebrow">
                                <span className="page-header__eyebrow__icon">
                                    <Package size={14} aria-hidden />
                                </span>
                                Catalog
                            </span>
                            <h1 className="page-header__title">Loading…</h1>
                        </div>
                    </div>
                </header>
                <section className="section-card">
                    <TableSkeleton rows={3} columns={3} />
                </section>
            </div>
        );
    }

    if (productQ.isError || !productQ.data) {
        const notFound = productQ.error instanceof ApiError && productQ.error.status === 404;
        return (
            <div className="stack-lg">
                <section className="section-card">
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">
                            {notFound ? "Product not found" : "Could not load product"}
                        </div>
                        <div className="empty-state__body">
                            {notFound
                                ? "That product slug does not exist."
                                : productQ.error instanceof Error
                                    ? productQ.error.message
                                    : "Unknown error."}
                        </div>
                        <Link to="/products" className="btn btn--ghost" style={{ marginTop: "var(--space-3)" }}>
                            <ArrowLeft size={14} />
                            <span>Back to products</span>
                        </Link>
                    </div>
                </section>
            </div>
        );
    }

    const product = productQ.data;

    async function toggleCapability(cap: string): Promise<void> {
        const current = new Set(product.capabilities);
        if (current.has(cap)) current.delete(cap);
        else current.add(cap);
        try {
            await setCaps.mutateAsync({ slug: product.slug, capabilities: Array.from(current) });
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to update capabilities");
        }
    }

    async function onDelete(): Promise<void> {
        if (product.licenseCount > 0) return;
        if (!window.confirm(`Delete product “${product.name}”? This cannot be undone.`)) return;
        try {
            await deleteProduct.mutateAsync(product.slug);
            toast.success("Product deleted");
            void navigate({ to: "/products" });
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to delete product");
        }
    }

    const deleteBlocked = product.licenseCount > 0;

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon">
                                <Package size={14} aria-hidden />
                            </span>
                            <Link to="/products" className="page-header__eyebrow__crumb">
                                Products
                            </Link>
                            <ChevronRight size={12} aria-hidden />
                            <span>{product.slug}</span>
                        </span>
                        <h1 className="page-header__title">{product.name}</h1>
                        <p className="page-header__description">
                            <code className="type-mono-sm">{product.slug}</code>
                            {" · "}
                            <span className="text-text-muted">{product.vendor}</span>
                        </p>
                    </div>
                    <div className="page-header__actions cluster-xs">
                        <button
                            type="button"
                            className="btn btn--primary press-crush"
                            onClick={() => setEditOpen(true)}
                        >
                            <Pencil size={14} />
                            <span>Edit</span>
                        </button>
                        <button
                            type="button"
                            className="btn btn--ghost"
                            onClick={() => void onDelete()}
                            disabled={deleteBlocked || deleteProduct.isPending}
                            title={
                                deleteBlocked
                                    ? `Cannot delete — ${product.licenseCount} license(s) still reference this product`
                                    : "Delete product"
                            }
                        >
                            <Trash2 size={14} />
                            <span>Delete</span>
                        </button>
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className={`status-pill ${STATUS_PILL[product.status]}`}>{product.status}</span>
                    <span className="page-header__meta__item">
                        <span className="chip">{product.model}</span>
                    </span>
                    <span className="page-header__meta__item tabular-nums">
                        {product.licenseCount} licenses
                    </span>
                    <span className="page-header__meta__item">Created {formatDate(product.createdAt)}</span>
                </div>
            </header>

            {/* Capabilities */}
            <section className="section-card" aria-label="Capabilities">
                <header className="section-card__header">
                    <div>
                        <div className="section-card__title">Capabilities</div>
                        <div className="section-card__description">
                            Which categories of intel this product can consume. Licenses issued against
                            this product can only grant IoC types that map to one of these capabilities.
                        </div>
                    </div>
                </header>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: "var(--space-3)",
                        padding: "var(--space-4)",
                    }}
                >
                    {capsQ.data?.map((c) => {
                        const active = product.capabilities.includes(c.slug);
                        return (
                            <button
                                key={c.slug}
                                type="button"
                                onClick={() => void toggleCapability(c.slug)}
                                aria-pressed={active}
                                className="metric-card"
                                style={{
                                    cursor: "pointer",
                                    textAlign: "left",
                                    borderColor: active ? "var(--color-accent)" : undefined,
                                    background: active ? "var(--color-accent-soft)" : undefined,
                                    opacity: setCaps.isPending ? 0.6 : 1,
                                }}
                                disabled={setCaps.isPending}
                            >
                                <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                                    <span className="text-13" style={{ fontWeight: 500 }}>
                                        {c.name}
                                    </span>
                                    <span
                                        className={`chip ${active ? "chip--accent" : "chip--subtle"}`}
                                        style={{ height: 20 }}
                                    >
                                        {active ? "on" : "off"}
                                    </span>
                                </div>
                                <div className="text-12 text-text-muted" style={{ marginTop: "var(--space-1)" }}>
                                    {c.description}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Description */}
            <section className="section-card" aria-label="Description">
                <header className="section-card__header">
                    <div>
                        <div className="section-card__title">Description</div>
                        <div className="section-card__description">Plain-text product description.</div>
                    </div>
                </header>
                <div style={{ padding: "var(--space-4)" }}>
                    {product.description.trim() === "" ? (
                        <span className="text-13 text-text-subtle">No description yet.</span>
                    ) : (
                        <p className="text-13" style={{ whiteSpace: "pre-line" }}>
                            {product.description}
                        </p>
                    )}
                </div>
            </section>

            {/* Licenses */}
            <section className="section-card" aria-label="Licenses">
                <header className="section-card__header">
                    <div>
                        <div className="section-card__title">Licenses</div>
                        <div className="section-card__description">
                            Licenses issued against this product.
                        </div>
                    </div>
                </header>
                {licensesQ.isLoading ? (
                    <TableSkeleton rows={3} columns={4} />
                ) : productLicenses.length === 0 ? (
                    <div className="empty-state">
                        <KeyRound size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No licenses yet</div>
                        <div className="empty-state__body">
                            Issue licenses from the{" "}
                            <Link to="/licenses" className="link">
                                Licenses
                            </Link>{" "}
                            page.
                        </div>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Tenant</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Status</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Expires</th>
                                    <th style={{ textAlign: "right", whiteSpace: "nowrap", width: 1 }}>Keys</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productLicenses.map((l) => (
                                    <tr
                                        key={l.id}
                                        onClick={() => setActiveLicense(l)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td className="text-13">{l.name}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <code className="type-mono-sm">{l.tenantId.slice(0, 8)}</code>
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <span className={`status-pill ${LICENSE_STATUS_PILL[l.status]}`}>
                                                {l.status}
                                            </span>
                                        </td>
                                        <td className="text-12 text-text-muted" style={{ whiteSpace: "nowrap" }}>
                                            {l.expiresAt ? formatDate(l.expiresAt) : "—"}
                                        </td>
                                        <td
                                            className="text-13 text-text-muted tabular"
                                            style={{ textAlign: "right" }}
                                        >
                                            {l.apiKeyCount}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <EditProductDialog product={product} open={editOpen} onOpenChange={setEditOpen} />
            <LicenseDetailDialog license={activeLicense} onClose={() => setActiveLicense(null)} />
        </div>
    );
}
